import React from 'react';
import { Box, Typography, Card, Chip, Stack } from '@mui/material';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Task } from '../../types';
import KanbanCard from './KanbanCard';

interface KanbanColumnProps {
  status: string;
  tasks: Task[];
  projectId: number;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status,
  tasks,
  projectId,
}) => {
  const getColumnColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'not started':
        return '#f0f0f0';
      case 'in progress':
        return '#e3f2fd';
      case 'completed':
        return '#e8f5e9';
      case 'on hold':
        return '#fff3e0';
      case 'cancelled':
        return '#ffebee';
      default:
        return '#f5f5f5';
    }
  };

  const getStatusBadgeColor = (status: string): any => {
    switch (status.toLowerCase()) {
      case 'not started':
        return 'default';
      case 'in progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'on hold':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Droppable droppableId={status} type="TASK">
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            minWidth: 300,
            bgcolor: getColumnColor(status),
            borderRadius: 1,
            p: 2,
            flex: '0 0 300px',
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            transition: 'background-color 0.2s',
            backgroundColor: snapshot.isDraggingOver
              ? `${getColumnColor(status)}dd`
              : getColumnColor(status),
            border: snapshot.isDraggingOver ? '2px solid #1976d2' : '1px solid #e0e0e0',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {status}
            </Typography>
            <Chip label={tasks.length} size="small" />
          </Stack>

          <Stack gap={1}>
            {tasks.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: '#999', textAlign: 'center', py: 2 }}
              >
                No tasks
              </Typography>
            ) : (
              tasks.map((task, index) => (
                <Draggable
                  key={`task-${task.id}`}
                  draggableId={`${task.id}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <KanbanCard
                        task={task}
                        isDragging={snapshot.isDragging}
                      />
                    </div>
                  )}
                </Draggable>
              ))
            )}
          </Stack>
          {provided.placeholder}
        </Box>
      )}
    </Droppable>
  );
};

export default KanbanColumn;
